import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import { skillsForRoles } from '@truecare/schemas';
import { Alert, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';

/**
 * Phase 17 (change-list) — declarable-skills checkboxes in the experience area.
 *
 * Renders only the skills the caregiver's roles make eligible (shared
 * `SKILL_CATALOG` via `skillsForRoles`), so e.g. palliative-care shows for
 * RN/RPN/PSW/Companion and footcare for RPN/RN. Toggling saves immediately
 * (full-set PUT /me/caregiver/skills); on error the checkbox reverts.
 *
 * Editable only when the profile is editable (APPLYING / ONBOARDING / ACTIVE);
 * otherwise the boxes render disabled so the caregiver still sees their skills.
 */
export function CaregiverSkillsSection({
  roleCodes,
  currentSkills,
  editable,
}: {
  roleCodes: string[];
  currentSkills: string[];
  editable: boolean;
}) {
  const qc = useQueryClient();
  const eligible = skillsForRoles(roleCodes);
  const eligibleCodes = new Set(eligible.map((s) => s.code));
  // Track only eligible skills — the full-set PUT must never carry a code the
  // caregiver's roles don't allow (the backend 400s on that).
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(currentSkills.filter((c) => eligibleCodes.has(c))),
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (codes: string[]) =>
      unwrap(api.PUT('/api/v1/me/caregiver/skills', { body: { skillCodes: codes } })),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['caregiver', 'me'] });
      void qc.invalidateQueries({ queryKey: ['caregiver', 'application-readiness'] });
    },
    onError: (e, _codes, ctx) => {
      // Revert to the pre-toggle set the mutation captured.
      if (ctx && ctx instanceof Set) setSelected(ctx as Set<string>);
      setError(messageOf(e, 'Could not save your skills.'));
    },
  });

  // No skills apply to these roles (e.g. DSW / Cook) — render nothing.
  if (eligible.length === 0) return null;

  function toggle(code: string): void {
    const prev = new Set(selected);
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSelected(next);
    // Pass `prev` as onError context so a failed save reverts the checkbox.
    mutation.mutate([...next], { onError: () => setSelected(prev) });
  }

  return (
    <Card padding="lg">
      <CardHeader
        title="Skills & experience"
        description="Tell buyers about relevant experience. These help match you to the right shifts."
      />
      {error && (
        <Alert tone="danger" title="Couldn't save">
          {error}
        </Alert>
      )}
      <div className="mt-3 flex flex-col gap-2">
        {eligible.map((s) => (
          <label
            key={s.code}
            className="flex items-center gap-2 font-body text-sm text-forest"
          >
            <input
              type="checkbox"
              checked={selected.has(s.code)}
              disabled={!editable || mutation.isPending}
              onChange={() => toggle(s.code)}
              data-testid={`caregiver-skill-${s.code}`}
            />
            {s.label}
          </label>
        ))}
      </div>
    </Card>
  );
}
